import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
  Typography,
  CircularProgress,
  alpha
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ClientResponseInteraction } from "../types/interactions";
import { ClientResponseRow } from "./ClientResponseRow";

interface ClientResponseTableProps {
  interactions: ClientResponseInteraction[];
  isLoading: boolean;
  error: string | null;
  onUpdateResponse: (id: number, responseStatus: string | null, responseNote: string | null) => void;
}

export const ClientResponseTable = ({
  interactions,
  isLoading,
  error,
  onUpdateResponse
}: ClientResponseTableProps) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Box sx={{ p: 8, textAlign: "center" }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={40} thickness={4} />
          <Typography variant="body1" color="text.secondary" fontWeight={500}>
            Loading interactions...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 8, textAlign: "center" }}>
        <Typography variant="h6" fontWeight={700} color="error">
          Error Loading Data
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
      </Box>
    );
  }

  if (interactions.length === 0) {
    return (
      <Box sx={{ p: 10, textAlign: "center" }}>
        <Stack spacing={2} alignItems="center">
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "12px",
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
                theme.palette.primary.main,
                0.12
              )} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}
          >
            <Typography variant="h3">📋</Typography>
          </Box>
          <Typography variant="h6" fontWeight={600}>
            No Interactions Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
            No order received or closed interactions in the selected date range
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <TableContainer sx={{ overflow: "auto" }}>
      <Table sx={{ tableLayout: "fixed", minWidth: 1600 }}>
        <TableHead>
          <TableRow
            sx={{
              bgcolor: alpha(theme.palette.grey[100], 0.5),
              borderBottom: `2px solid ${theme.palette.divider}`,
              "& th": {
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                py: 1.5,
                borderBottom: "none"
              }
            }}
          >
            <TableCell width="200px">Client</TableCell>
            <TableCell width="200px">Reason</TableCell>
            <TableCell width="110px">Date</TableCell>
            <TableCell width="130px" sx={{ borderRight: `2px solid ${theme.palette.divider}` }}>Interaction</TableCell>
            <TableCell width="450px">Response</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {interactions.map((interaction) => (
            <ClientResponseRow
              key={interaction.id}
              interaction={interaction}
              onUpdateResponse={onUpdateResponse}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
